import {
  Box,
  List,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  useTheme,
} from '@mui/material';
import { MessageCorrespondents } from '_api/types';
import { SearchInput } from 'components/atoms';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { FaCircle } from 'react-icons/fa';
import { useMessage } from 'utils/hooks';

import { StyledListItem, StyledMessageCorrespondentText } from './style';
import { MessageListProps } from './types';

export const MessageList = ({ isMenu = false, onClick }: MessageListProps) => {
  const theme = useTheme();
  const { correspondents, unreadMessageIdList } = useMessage();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCorrespondents = useMemo(() => {
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    const searchCorrespondents =
      (searchTerm: string) =>
      ({ firstName, lastName, id }: MessageCorrespondents) =>
        `${firstName} ${lastName}`.toLowerCase().includes(searchTerm) ||
        id.toString().includes(searchTerm);

    return correspondents.filter(searchCorrespondents(lowercasedSearchTerm));
  }, [searchTerm, correspondents]);

  const getTimeFromNow = (timestamp: number) => {
    const now = dayjs();
    const messageDateTime = dayjs(timestamp);
    const differenceMin = now.diff(messageDateTime, 'm');
    if (differenceMin < 60) {
      return differenceMin + 'm';
    } else {
      const differenceHr = now.diff(messageDateTime, 'h');
      return differenceHr < 24
        ? differenceHr + 'h'
        : now.diff(messageDateTime, 'd') + 'd';
    }
  };

  return (
    <Box
      sx={{
        // @ts-ignore
        backgroundColor: theme.palette.grey.light,
        padding: 5,
        maxHeight: isMenu ? '100%' : '100%',
        minHeight: isMenu ? '100%' : 'auto',
        overflowY: 'auto',
      }}
    >
      <Box>
        <SearchInput
          placeholder="Search Correspondents"
          onSearch={setSearchTerm}
          inputCss={{ height: '2rem' }}
          showIcon={false}
          wrapperCss={{ width: '100%' }}
        />
      </Box>
      <List sx={{ padding: 0 }}>
        {filteredCorrespondents.map((correspondent) => (
          <StyledListItem
            key={correspondent.id}
            alignItems="flex-start"
            onClick={() => onClick(correspondent.id)}
          >
            <ListItemAvatar>
              <Avatar
                alt={correspondent.firstName[0] + correspondent.lastName[0]}
                src={correspondent.fullImageUrl}
              />
            </ListItemAvatar>
            <ListItemText
              primary={
                <React.Fragment>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      {unreadMessageIdList.includes(correspondent.id) ? (
                        <FaCircle color={theme.palette.primary.main} fontSize={8} />
                      ) : null}
                    </Box>
                    <StyledMessageCorrespondentText
                      variant="body1"
                      fontWeight="bold"
                      sx={
                        unreadMessageIdList.includes(correspondent.id)
                          ? { marginLeft: 2 }
                          : { marginLeft: 7 }
                      }
                    >
                      {correspondent.firstName} {correspondent.lastName}
                    </StyledMessageCorrespondentText>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ marginLeft: 'auto' }}
                    >
                      {getTimeFromNow(correspondent.latest)}
                    </Typography>
                  </Box>
                  <StyledMessageCorrespondentText variant="body2" sx={{ marginLeft: 7 }}>
                    {correspondent.message.message}
                  </StyledMessageCorrespondentText>
                </React.Fragment>
              }
            />
          </StyledListItem>
        ))}
      </List>
    </Box>
  );
};
